import { useState, useEffect, useRef } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { Spinner, Icon } from '../ui';
import { MAPBOX_TOKEN, ensureMapboxLoaded } from './ParcelsMapTab';
import { BASEMAP_BY_ID, DEFAULT_BASEMAP_ID } from '../../constants/mapLayers';

// ---------------------------------------------------------------------------
// ProspectParcelPicker — pick the parcels attached to a prospect using the map.
// Renders the owning DealDetail's parcels on a Mapbox map; click a parcel to
// toggle it in/out of the prospect's selection. A synced checklist below the
// map covers accessibility and any parcels that have no boundary geometry yet.
//
// Props:
//   dealDetailId  — the R/E DealDetail whose parcels are selectable
//   value         — array of selected parcelIDs (controlled)
//   onChange(ids) — called with the next array of selected parcelIDs
// ---------------------------------------------------------------------------
export const ProspectParcelPicker = ({ dealDetailId, value, onChange }) => {
  const selected = value || [];

  const mapContainer = useRef(null);
  const mapRef       = useRef(null);
  const [mapState, setMapState] = useState('loading'); // loading | ready | no-token | error
  const [parcels,  setParcels]  = useState(null);       // list from GET /parcels
  const [loadingList, setLoadingList] = useState(true);

  // Refs mirror the latest props so the (once-registered) map click handler and
  // the fit-bounds effect always read current values without re-binding.
  const selectedRef = useRef(selected);
  const onChangeRef = useRef(onChange);
  selectedRef.current = selected;
  onChangeRef.current = onChange;

  const toggle = (parcelId) => {
    if (!parcelId) return;
    const cur  = selectedRef.current || [];
    const next = cur.includes(parcelId) ? cur.filter(id => id !== parcelId) : [...cur, parcelId];
    onChangeRef.current?.(next);
  };

  // ── Load the parcel list for the checklist ────────────────────────────────
  useEffect(() => {
    let alive = true;
    setLoadingList(true);
    api.get(`parcels?dealDetailId=${dealDetailId}`)
      .then(rows => { if (alive) setParcels(rows || []); })
      .catch(() => { if (alive) setParcels([]); })
      .finally(() => { if (alive) setLoadingList(false); });
    return () => { alive = false; };
  }, [dealDetailId]);

  // ── Init map + owned-parcel layers ────────────────────────────────────────
  useEffect(() => {
    if (!MAPBOX_TOKEN) { setMapState('no-token'); return; }
    let cancelled = false;

    ensureMapboxLoaded().then(async () => {
      if (cancelled || !mapContainer.current || mapRef.current) return;
      try {
        window.mapboxgl.accessToken = MAPBOX_TOKEN;
        const map = new window.mapboxgl.Map({
          container: mapContainer.current,
          style: BASEMAP_BY_ID[DEFAULT_BASEMAP_ID].style,
          zoom: 12,
          center: [-98.5, 39.5],
        });
        map.addControl(new window.mapboxgl.NavigationControl(), 'top-right');
        mapRef.current = map;

        map.on('load', async () => {
          if (cancelled) return;
          let fc = { type: 'FeatureCollection', features: [] };
          try { fc = await api.get(`parcel-map?dealDetailId=${dealDetailId}`); } catch { /* no geometry */ }

          map.addSource('pp-parcels', { type: 'geojson', data: fc });
          map.addLayer({ id: 'pp-fill', type: 'fill', source: 'pp-parcels',
            paint: { 'fill-color': '#22d3ee', 'fill-opacity': 0.25 } });
          map.addLayer({ id: 'pp-outline', type: 'line', source: 'pp-parcels',
            paint: { 'line-color': '#0891b2', 'line-width': 2 } });
          map.addLayer({ id: 'pp-selected', type: 'fill', source: 'pp-parcels',
            filter: ['in', ['get', 'parcelId'], ['literal', selectedRef.current]],
            paint: { 'fill-color': '#f59e0b', 'fill-opacity': 0.55 } });
          map.addLayer({
            id: 'pp-labels', type: 'symbol', source: 'pp-parcels', minzoom: 12,
            layout: {
              'text-field': ['coalesce', ['get', 'parcelNumber'], ''],
              'text-size': 11,
              'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
              'text-allow-overlap': false,
            },
            paint: { 'text-color': '#ffffff', 'text-halo-color': '#1a3844', 'text-halo-width': 1.5 },
          });

          map.on('click', 'pp-fill', (e) => toggle(e.features?.[0]?.properties?.parcelId));
          map.on('mouseenter', 'pp-fill', () => { map.getCanvas().style.cursor = 'pointer'; });
          map.on('mouseleave', 'pp-fill', () => { map.getCanvas().style.cursor = ''; });

          // Fit to the parcel extents.
          const coords = [];
          (fc.features || []).forEach(f => {
            if (f.geometry?.type === 'Polygon')           coords.push(...f.geometry.coordinates[0]);
            else if (f.geometry?.type === 'MultiPolygon') f.geometry.coordinates.forEach(p => coords.push(...p[0]));
          });
          if (coords.length > 0) {
            const lngs = coords.map(c => c[0]), lats = coords.map(c => c[1]);
            map.fitBounds([[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
              { padding: 40, duration: 600 });
          }
          setMapState('ready');
        });
      } catch (err) {
        console.error('Prospect parcel picker map failed:', err);
        setMapState('error');
      }
    }).catch(() => { if (!cancelled) setMapState('error'); });

    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, [dealDetailId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Keep the highlight layer in sync with the selection ───────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (mapState !== 'ready' || !map || !map.getLayer('pp-selected')) return;
    map.setFilter('pp-selected', ['in', ['get', 'parcelId'], ['literal', selected]]);
  }, [selected, mapState]);

  const list = parcels || [];

  return (
    <div>
      <div style={{ fontSize: 12, color: B.textMd, marginBottom: 6 }}>
        {MAPBOX_TOKEN
          ? 'Click parcels on the map to include them in this prospect, or use the list below.'
          : 'Select the parcels included in this prospect.'}
      </div>

      {MAPBOX_TOKEN && (
        <div style={{ position: 'relative', height: 260, marginBottom: 10 }}>
          <div ref={mapContainer} style={{ height: '100%', borderRadius: 8, overflow: 'hidden', border: `1px solid ${B.border}` }} />
          {(mapState === 'loading' || mapState === 'error') && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: B.surfaceMuted, borderRadius: 8, fontSize: 12, color: B.textMd }}>
              {mapState === 'error'
                ? <span><Icon name="map" size={16} /> The map couldn’t load — use the list below.</span>
                : <Spinner />}
            </div>
          )}
        </div>
      )}

      <div style={{ border: `1px solid ${B.border}`, borderRadius: 8, maxHeight: 160, overflowY: 'auto' }}>
        {loadingList ? (
          <div style={{ padding: 12 }}><Spinner /></div>
        ) : list.length === 0 ? (
          <div style={{ padding: '12px', fontSize: 12, color: B.textLt, textAlign: 'center' }}>
            No parcels on this property yet. Add parcels on the Parcels or Map tab first.
          </div>
        ) : list.map(p => {
          const on = selected.includes(p.parcelID);
          return (
            <label key={p.parcelID}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 12, color: B.text, borderBottom: `1px solid ${B.border}`, background: on ? '#fff7ed' : 'transparent' }}>
              <input type="checkbox" checked={on} onChange={() => toggle(p.parcelID)} />
              <span style={{ flex: 1 }}>{p.parcelNumber || '(unnamed parcel)'}</span>
              <span style={{ color: B.textLt }}>
                {(p.regridAcres ?? p.acres) != null ? `${parseFloat(p.regridAcres ?? p.acres).toFixed(2)} ac` : '—'}
              </span>
            </label>
          );
        })}
      </div>
      {selected.length > 0 && (
        <div style={{ fontSize: 12, color: B.textMd, marginTop: 6 }}>{selected.length} parcel{selected.length !== 1 ? 's' : ''} selected</div>
      )}
    </div>
  );
};
